import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

// Minimal stage editor: reorder, rename, toggle hidden. Edits a local draft and
// PUTs the full list once on Save (the server replaces the whole set).
//   stages   full stage list incl. hidden, sorted by order
//   onSave   (stages) => Promise<{stages}>   — caller persists and re-derives columns
//   counts   { [key]: n }                    — cards per stage, shown so hiding is informed
export default function StageConfigDialog({ open, onOpenChange, stages, counts = {}, onSave }) {
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDraft(stages.map(s => ({ ...s }))); }, [open, stages]);

  const update = (i, patch) => setDraft(d => d.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const swap = (i, j) => setDraft(d => {
    if (j < 0 || j >= d.length) return d;
    const next = d.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    if (draft.some(s => !s.label.trim())) { toast({ title: 'Every stage needs a label' }); return; }
    setSaving(true);
    try {
      await onSave(draft.map((s, i) => ({ ...s, label: s.label.trim(), order: i })));
      toast({ title: 'Stages saved' });
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || String(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="pipeline-stage-config">
        <DialogHeader><DialogTitle className="font-serif" style={{ color: '#070B5F' }}>Configure Stages</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Hidden stages keep their cards; they're listed above the board instead of in a column.</p>
        <div className="overflow-y-auto -mx-1 px-1 space-y-1.5">
          {draft.map((s, i) => (
            <div key={s.key} data-testid={`stage-cfg-row-${s.key}`}
              className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${s.hidden ? 'bg-slate-50 border-slate-100 opacity-70' : 'bg-white border-slate-200'}`}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <Input value={s.label} onChange={(e) => update(i, { label: e.target.value })}
                className="h-8 text-sm flex-1" data-testid={`stage-cfg-label-${s.key}`} />
              <span className="text-[11px] text-slate-400 w-8 text-right tabular-nums">{counts[s.key] || 0}</span>
              <button type="button" onClick={() => swap(i, i - 1)} disabled={i === 0} title="Move up"
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`stage-cfg-up-${s.key}`}>
                <ArrowUp size={14} />
              </button>
              <button type="button" onClick={() => swap(i, i + 1)} disabled={i === draft.length - 1} title="Move down"
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`stage-cfg-down-${s.key}`}>
                <ArrowDown size={14} />
              </button>
              <button type="button" onClick={() => update(i, { hidden: !s.hidden })} title={s.hidden ? 'Show stage' : 'Hide stage'}
                aria-pressed={!!s.hidden}
                className={`p-1 rounded hover:bg-slate-100 ${s.hidden ? 'text-slate-400' : 'text-[#070B5F]'}`} data-testid={`stage-cfg-hide-${s.key}`}>
                {s.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-outline" onClick={() => onOpenChange(false)}>Cancel</button>
          <button disabled={saving} onClick={save} data-testid="stage-cfg-save" className="btn-gold">
            {saving && <Loader2 className="animate-spin" size={14} />} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
